import { NudgingDocxLogo, Spinner, styled, theme } from 'design-system';
import { TUploadErrorCode, UploadButtonNudge } from 'preflight-core';
import React, { useState } from 'react';

import { useEventTracking } from '../contexts/tracking-context';
import { HeroErrorNudge } from '../partials/heros/hero-error-nudge';
import { UploadState } from '../utils/use-upload-document';

const StyledFileListBullet = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
  fontWeight: 400,
  color: 'rgba(73, 87, 112, 1)',
  fontFamily: 'IBM Plex Sans',
  fontSize: '14px',
  fontStyle: 'normal',
  width: 530,

  '&::before': {
    content: '""',
    width: '3.5px',
    height: '3.5px',
    backgroundColor: 'rgba(73, 87, 112, 1)',
    borderRadius: '100%',
    top: '24%',
    left: 0,
    transform: 'translateY(-50%) translateX(-300%)',
    position: 'absolute',
  },
});

const Container = styled('div', {
  flexShrink: 0,
  background: 'white',
  position: 'relative',
  padding: 40,

  zIndex: 999,
});

const SuggestionText = styled('div', {
  color: 'rgba(10, 31, 84, 1)',
  fontFamily: 'IBM Plex Serif',

  textAlign: 'center',
  fontSize: '20px',

  left: '50%',
  width: '100%',
  lineHeight: '40px',
  fontStyle: 'normal',
  fontWeight: 600,
  marginBottom: '20px',
  zIndex: 1,
});

const FileBetterTextHeading = styled('div', {
  fontFamily: 'IBM Plex Sans',
  color: 'rgba(73, 87, 112, 1)',
  fontStyle: 'normal',
  justifyContent: 'flex-start',
  alignContent: 'flex-start',
  margin: '20px 0',
  lineHeight: '22px',
  fontSize: '14px',
  fontWeight: 700,
  marginTop: 80,
});

const ListofFilesBullets = styled('div', {
  color: 'rgba(73, 87, 112, 1)',
  fontFamily: 'IBM Plex Sans',
  fontSize: '14px',
  fontStyle: 'normal',
  fontWeight: 400,
  lineHeight: '22px',
  textAlign: 'left',
  margin: '20px 0',
  width: 570,
  zIndex: 1,
});

const MainHeading = styled('div', {
  color: 'rgba(74, 85, 104, 1)',
  marginBottom: '20px',
  fontSize: '14px',
  fontStyle: 'normal',

  fontWeight: 500,
  lineHeight: '20px',

  textAlign: 'center',
  fontFamily: 'IBM Plex Sans',
});

const ErrorConatiner = styled('div', {
  marginTop: '40px',
  alignItems: 'center',
});

const NudgingDocxLogoOuter = styled('div', {
  display: 'flex',
  justifyContent: 'center',
  marginBottom: '40px',
});

const ButtonLinkContainer = styled('div', {
  textAlign: 'center',

  top: '50%',
  left: '50%',
});

const PdfLink = styled('a', {
  color: 'rgba(0, 98, 255, 1)',
  fontSize: '16px',

  display: 'flex',
  alignContent: 'center',
  marginTop: '20px',
  marginLeft: '265px',
  textDecoration: 'none',
  cursor: 'pointer',

  '&:hover': {
    cursor: 'pointer',
  },
});

const SpinnerBox = styled('a', {
  marginLeft: '80px',
});

type OnFileSelected = (file: File) => void;

export const NudgingDocx = ({
  uploadState,

  onFileSelected,
  onContinueWithPdfClick,
  uploadStateContinuePdf,
}: {
  uploadState: UploadState;
  onFileSelected: OnFileSelected;

  onContinueWithPdfClick: (param: boolean) => void;
  uploadStateContinuePdf?: {
    name: string;
    error?: { json: { errorCode: TUploadErrorCode; errorReason: string } };
    data?: { json: object };
  };
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const { trackEvent } = useEventTracking();
  const handlePdfLinkClick = () => {
    onContinueWithPdfClick(true);
    setIsLoading(true);
    setTimeout(() => {
      setIsLoading(false);
    }, 1000000);
  };
  const [isButtonClicked, setIsButtonClicked] = useState(false);

  const handleUploadButtonClick = () => {
    trackEvent([
      'Manuscript upload intent',
      'Upload click',
      'Run pre-submission checks Free',
      {
        Section: 'Section 1',
      },
    ]);
  };

  return (
    <Container>
      <MainHeading>
        It appears that the PDF file you uploaded <br />
        was created from a .doc/.docx file.
      </MainHeading>
      <SuggestionText>
        We suggest you upload the .doc/.docx version of this file
      </SuggestionText>

      {uploadState.name === 'error' ||
      uploadStateContinuePdf?.name === 'error' ? (
        <ErrorConatiner>
          <HeroErrorNudge
            onFileSelected={onFileSelected}
            uploadState={uploadState}
            uploadStateContinuePdf={uploadStateContinuePdf}
            showSubmission={false}
            submissionUrl={''}
          />
        </ErrorConatiner>
      ) : (
        <>
          <NudgingDocxLogoOuter>
            <NudgingDocxLogo />
          </NudgingDocxLogoOuter>

          <ButtonLinkContainer>
            <UploadButtonNudge
              id="Run-Pre-Submission Checks-Free"
              disabled={
                isButtonClicked &&
                (uploadState.name === 'loading' ||
                  uploadState.name === 'success')
              }
              onClick={handleUploadButtonClick}
              onFileSelected={(file: File) => {
                setIsButtonClicked(true);
                onFileSelected(file);
              }}
              size="small"
              text="Upload .doc/.docx file version"
              style={{
                fontSize: '20px',
                paddingTop: '20px',
                paddingBottom: '20px',
              }}
            />
            <PdfLink onClick={handlePdfLinkClick}>
              {isLoading ? (
                <>
                  <SpinnerBox>
                    <Spinner size={16} color={theme.colors.blue10} />
                  </SpinnerBox>
                </>
              ) : (
                'Continue with PDF'
              )}
            </PdfLink>
          </ButtonLinkContainer>
        </>
      )}

      <FileBetterTextHeading>
        Why are we suggesting you to upload a Word file?
      </FileBetterTextHeading>
      <ListofFilesBullets>
        <ul>
          <StyledFileListBullet>
            Downloaded edited file created basis your manuscript is produced in
            the same format as the uploaded file; and contains all the language
            edit suggestions in it.
          </StyledFileListBullet>
          <StyledFileListBullet>
            It is easier to accept or reject in a .doc/.docx file format. In
            PDF, you will have to copy-paste to the original Word file.
          </StyledFileListBullet>
        </ul>
        <text>
          Thus, for your convenience, as the Word file exists, we suggest you
          upload the .doc/.docx version of the manuscript.
        </text>
      </ListofFilesBullets>
    </Container>
  );
};

NudgingDocx.displayName = 'NudgingDocx';
